import { type TezosToolkit } from "@taquito/taquito";
import {
  type ChainFullId,
  ChainFullIds,
  type ChainId,
  type ChainRegistry,
  type LedgerSigner,
  type MultichainTokenBalance,
  type SignTransactionOptions,
  type TransactionToSend,
  parseChainFullId,
} from "@umami/chains";
import { type JsonRpcProvider, type TransactionResponse, type Wallet } from "ethers";

import { BridgeDirection } from "../bridge";
import { type GaslessQuoteResponse, executeGaslessSwap } from "./gaslessSwap";
import { executeLiFiRoute } from "./lifiExecution";
import { type LiFiQuoteResponse } from "./lifiQuote";
import { type ZeroXQuoteResponse } from "./quote";
import { executeSwap } from "./swap";
import { type BridgeNetwork, CHAIN_ID_MAPPING, getTokenAddress } from "../config";
import { type TezosBridge } from "../tezos-bridge";
import { tokenAmountToWei } from "../utils/token-conversion";

export interface SwapExecutionParams {
  sellToken: MultichainTokenBalance;
  buyToken: MultichainTokenBalance;
  sellAmount: string;
  buyAmount: string;
  direction: BridgeDirection;
  selectedQuote: any;
  zeroXQuote?: ZeroXQuoteResponse | null;
  gaslessQuote?: GaslessQuoteResponse | null;
  lifiQuote?: LiFiQuoteResponse | null;
  isGaslessSwap?: boolean;
  currentNetwork: { name: string };
  currentAccount: any;
  tezosToolkit: TezosToolkit;
  options: SignTransactionOptions;
  isFastWithdrawal?: boolean;
  // Bridge dependencies
  bridge: {
    ethereumProvider: JsonRpcProvider;
    getErc20Contract: (address: string, provider: any) => any;
    getProvider: (chainId: ChainId) => any;
  };
  tezosBridge: TezosBridge;
  // Utility functions
  createSigner: (options: SignTransactionOptions, chainId?: ChainFullId) => Promise<any>;
  prepareApproveToken: (params: any) => Promise<any>;
  prepareBridgeToken: (params: any) => Promise<any>;
  registry: ChainRegistry;
  onConfirm: () => Promise<void>;
  getNetworkChain: (chainId: ChainFullId) => ChainId;
}

export class SwapExecutionServiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SwapExecutionServiceError";
  }
}

function isLedgerSigner(signer: any): signer is LedgerSigner {
  return signer?.type === "ledger";
}

export class SwapExecutionService {
  static async executeSwap(params: SwapExecutionParams): Promise<TransactionResponse | void> {
    const { direction, selectedQuote } = params;

    const isTezosEtherlinkSwap =
      direction === BridgeDirection.TezosToEtherlink ||
      direction === BridgeDirection.EtherlinkToTezos;

    if (isTezosEtherlinkSwap) {
      return this.executeTezosEtherlinkSwap(params);
    }

    switch (selectedQuote?.provider) {
      case "LiFi":
        return this.executeLiFiSwap(params);

      case "0x":
      case "Gasless":
        return this.executeZeroXSwap(params);

      case "LayerZero":
        return this.executeLayerZeroSwap(params);

      default:
        throw new SwapExecutionServiceError(
          `Unsupported swap provider: ${selectedQuote?.provider}`
        );
    }
  }

  private static async executeTezosEtherlinkSwap(
    params: SwapExecutionParams
  ): Promise<TransactionResponse | void> {
    const {
      sellAmount,
      direction,
      tezosToolkit,
      options,
      tezosBridge,
      createSigner,
      onConfirm,
      isFastWithdrawal,
      currentAccount,
      buyAmount,
    } = params;

    const signer = await createSigner(options);
    tezosBridge.initialize(tezosToolkit, signer);

    const decimals = direction === BridgeDirection.TezosToEtherlink ? 6 : 18;
    const amount = BigInt(parseFloat(sellAmount) * Math.pow(10, decimals));

    if (direction === BridgeDirection.TezosToEtherlink) {
      const { operationResult } = await tezosBridge.depositXtz(amount);
      await onConfirm();

      return operationResult as unknown as TransactionResponse;
    } else {
      if (isFastWithdrawal) {
        const tezosAddress = currentAccount.chains?.[ChainFullIds.TEZOS]?.address.pkh;

        if (!tezosAddress) {
          throw new SwapExecutionServiceError(
            "Missing Tezos address or fast withdrawal contract address"
          );
        }
        const tx = await tezosBridge.withdrawXtz(amount, {
          fastWithdrawal: true,
          tezosAddress,
          buyAmount,
        });
        await onConfirm();

        return tx.operationResult as unknown as TransactionResponse;
      } else {
        const { operationResult } = await tezosBridge.withdrawXtz(amount);
        await onConfirm();

        return operationResult as unknown as TransactionResponse;
      }
    }
  }

  private static async executeLiFiSwap(params: SwapExecutionParams): Promise<void> {
    const { sellToken, lifiQuote, options, createSigner, onConfirm, getNetworkChain } = params;

    if (!lifiQuote) {
      throw new SwapExecutionServiceError("LiFi quote is required for LiFi swaps");
    }

    const signer = await createSigner(options, sellToken.chainId);

    if (isLedgerSigner(signer)) {
      throw new SwapExecutionServiceError("Ledger signing for LiFi swaps is not yet supported");
    }

    const result = await executeLiFiRoute({
      route: lifiQuote.route,
      wallet: signer as Wallet,
      chainId: getNetworkChain(sellToken.chainId),
      onChainSwitch: async (targetChainId: number) => {
        const targetChain = [ChainFullIds.ETHEREUM, ChainFullIds.ETHERLINK].find(chainId => {
          const { identifier } = parseChainFullId(chainId);

          return identifier === targetChainId.toString();
        });

        const newSigner = await createSigner(options, targetChain);

        if (isLedgerSigner(newSigner)) {
          throw new SwapExecutionServiceError(
            "Ledger signing for LiFi chain switching is not yet supported"
          );
        }

        return newSigner;
      },
    });

    if (result.status === "failed") {
      throw new SwapExecutionServiceError(result.error || "LiFi execution failed");
    }

    await onConfirm();
  }

  private static async executeZeroXSwap(params: SwapExecutionParams): Promise<void> {
    const { gaslessQuote, isGaslessSwap = false } = params;

    if (isGaslessSwap && gaslessQuote) {
      return this.executeGaslessSwap(params);
    } else {
      return this.executeRegularSwap(params);
    }
  }

  private static async executeGaslessSwap(params: SwapExecutionParams): Promise<void> {
    const { sellToken, buyToken, sellAmount, options, createSigner, onConfirm } = params;

    const sellTokenAddress = sellToken.address;
    const buyTokenAddress = buyToken.address;

    if (!sellTokenAddress || !buyTokenAddress) {
      throw new SwapExecutionServiceError("Missing token addresses for gasless swap");
    }

    const sellAmountWei = (
      parseFloat(sellAmount) * Math.pow(10, sellToken.decimals || 18)
    ).toString();

    const signer = await createSigner(options, sellToken.chainId);

    if (isLedgerSigner(signer)) {
      throw new SwapExecutionServiceError("Ledger signing for gasless swaps is not yet supported");
    }

    await executeGaslessSwap(
      sellTokenAddress,
      buyTokenAddress,
      sellAmountWei,
      signer as Wallet,
      CHAIN_ID_MAPPING[sellToken.chainId as keyof typeof CHAIN_ID_MAPPING]
    );

    await onConfirm();
  }

  private static async executeRegularSwap(params: SwapExecutionParams): Promise<void> {
    const {
      sellToken,
      buyToken,
      sellAmount,
      buyAmount,
      currentNetwork,
      options,
      bridge,
      createSigner,
      onConfirm,
      getNetworkChain,
    } = params;

    const network: BridgeNetwork = currentNetwork.name === "mainnet" ? "mainnet" : "shadownet";
    const provider = bridge.ethereumProvider;
    const signer = await createSigner(options, sellToken.chainId);

    if (isLedgerSigner(signer)) {
      throw new SwapExecutionServiceError("Ledger signing for swaps is not yet supported");
    }

    await executeSwap({
      sellToken: {
        ...sellToken,
        chainId: getNetworkChain(sellToken.chainId),
        id: sellToken.address || "",
        name: sellToken.symbol,
        type: "ERC20",
        value: "0",
        address: sellToken.address || "",
      },
      buyToken: {
        ...buyToken,
        chainId: getNetworkChain(buyToken.chainId),
        id: buyToken.address || "",
        name: buyToken.symbol,
        type: "ERC20",
        value: "0",
        address: buyToken.address || "",
      },
      sellAmount,
      buyAmount,
      signer: signer as Wallet,
      provider,
      network,
    });

    await onConfirm();
  }

  private static async executeLayerZeroSwap(
    params: SwapExecutionParams
  ): Promise<TransactionResponse> {
    const {
      sellToken,
      sellAmount,
      direction,
      currentNetwork,
      currentAccount,
      options,
      bridge,
      createSigner,
      prepareApproveToken,
      prepareBridgeToken,
      registry,
      onConfirm,
      getNetworkChain,
    } = params;

    const network: BridgeNetwork = currentNetwork.name === "mainnet" ? "mainnet" : "shadownet";
    const tokenAddress = getTokenAddress(
      sellToken.symbol,
      network,
      getNetworkChain(sellToken.chainId)
    );

    if (!tokenAddress) {
      throw new SwapExecutionServiceError(
        `Token address not found for ${sellToken.symbol} on ${sellToken.chainId}`
      );
    }

    let decimals: number;

    if (sellToken.symbol === "ETH" && getNetworkChain(sellToken.chainId) === "ethereum") {
      decimals = 18;
    } else {
      const erc20Contract = bridge.getErc20Contract(
        tokenAddress,
        bridge.getProvider(getNetworkChain(sellToken.chainId))
      );
      decimals = await erc20Contract.decimals();
    }

    const amountWei = tokenAmountToWei(sellAmount, decimals);
    const amount = BigInt(amountWei);
    const recipientAddress = currentAccount.chains?.[ChainFullIds.ETHERLINK]?.address.pkh ?? "";

    // Handle approval
    const approveTx = await prepareApproveToken({
      direction,
      tokenAddress,
      amount,
      options,
      chainId: sellToken.chainId,
    });

    if (approveTx) {
      if (!approveTx.to || !approveTx.data) {
        throw new SwapExecutionServiceError(
          "Invalid approval transaction: missing contract address or data"
        );
      }

      const approveTxToSend: TransactionToSend = {
        to: approveTx.to,
        value: approveTx.value?.toString() || "0",
        data: approveTx.data,
        contract: approveTx.to,
      };

      const approveResponse = await registry.signAndSendTransaction(
        sellToken.chainId,
        approveTxToSend,
        options
      );

      const approveReceipt = await approveResponse.wait();

      if (!approveReceipt?.status) {
        throw new SwapExecutionServiceError("Token approval failed");
      }

      // Wait a bit to ensure the approval is processed
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Handle bridge transaction
    const bridgeTx = await prepareBridgeToken({
      direction,
      tokenAddress,
      amount,
      recipient: recipientAddress,
      options,
      chainId: sellToken.chainId,
      isNativeEth: sellToken.symbol === "ETH" && getNetworkChain(sellToken.chainId) === "ethereum",
    });

    if (!bridgeTx.to || !bridgeTx.data) {
      throw new SwapExecutionServiceError(
        "Invalid bridge transaction: missing contract address or data"
      );
    }

    const bridgeTxToSend: TransactionToSend = {
      to: bridgeTx.to,
      value: bridgeTx.value?.toString() || "0",
      data: bridgeTx.data,
      contract: bridgeTx.to,
    };

    const signer = await createSigner(options, sellToken.chainId);
    const bridgeResponse = await signer.sendTransaction({
      to: bridgeTxToSend.to,
      value: bridgeTxToSend.value,
      data: bridgeTxToSend.data,
    });

    const bridgeReceipt = await bridgeResponse.wait();

    if (!bridgeReceipt?.status) {
      throw new SwapExecutionServiceError("Bridge transaction failed");
    }

    if (isLedgerSigner(signer)) {
      await (signer as LedgerSigner).close();
    }

    await onConfirm();

    return bridgeResponse;
  }
}
